/**
 * เสียงเกมทั้งหมดสังเคราะห์สดด้วย Web Audio API
 * ไม่มีไฟล์เสียงให้โหลด = เปิดเกมเร็ว ขนาดเล็ก และปรับโทนได้จากโค้ดตรง ๆ
 *
 * เบราว์เซอร์ห้ามเล่นเสียงก่อนผู้ใช้แตะหน้าจอ เราจึงสร้าง AudioContext
 * ตอนมี interaction ครั้งแรกเท่านั้น (ดู unlock())
 */

const STORAGE_KEY = 'millo.sound.v1'

let ctx: AudioContext | null = null
let master: GainNode | null = null
let enabled = readEnabled()
let unlocked = false

function readEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'off'
  } catch {
    return true
  }
}

export function soundEnabled(): boolean {
  return enabled
}

export function setSoundEnabled(on: boolean): void {
  enabled = on
  try {
    localStorage.setItem(STORAGE_KEY, on ? 'on' : 'off')
  } catch {
    /* โหมดส่วนตัวอาจบล็อก */
  }
  if (on) unlock()
  if (master && ctx) master.gain.setTargetAtTime(on ? 0.9 : 0, ctx.currentTime, 0.02)
}

/** เรียกจาก event ของผู้ใช้เท่านั้น (click / keydown / touch) */
export function unlock(): void {
  if (unlocked) {
    void ctx?.resume()
    return
  }
  try {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    ctx = new Ctor()
    master = ctx.createGain()
    master.gain.value = enabled ? 0.9 : 0
    master.connect(ctx.destination)
    unlocked = true
  } catch {
    ctx = null
  }
}

/** AudioContext ที่ใช้ร่วมกันทั้งเสียงเอฟเฟกต์และดนตรี (เบราว์เซอร์จำกัดจำนวน context) */
export function audioContext(): AudioContext | null {
  return ctx
}

/**
 * บัสหลักสำหรับโมดูลเสียงอื่น (เสียงพูดตัวละคร)
 * ต่อผ่านตัวนี้เท่านั้น อย่าต่อเข้า ctx.destination ตรง ๆ ไม่งั้นปุ่มปิดเสียงจะไม่มีผล
 */
export function masterBus(): GainNode | null {
  if (!enabled || !ctx || !master) return null
  if (ctx.state === 'suspended') void ctx.resume()
  return master
}

function ready(): boolean {
  if (!enabled || !ctx || !master) return false
  if (ctx.state === 'suspended') void ctx.resume()
  return true
}

/* ------------------------------------------------------------------ */
/* อิฐก่อสร้างเสียง                                                   */
/* ------------------------------------------------------------------ */

interface ToneOpts {
  freq: number
  /** ความถี่ปลายทาง ถ้าอยากให้กวาดเสียง */
  to?: number
  dur: number
  type?: OscillatorType
  gain?: number
  delay?: number
  /** ครึ่งเสียงของการสั่น (vibrato) */
  detune?: number
}

function tone(o: ToneOpts): void {
  if (!ready() || !ctx || !master) return
  const t0 = ctx.currentTime + (o.delay ?? 0)
  const osc = ctx.createOscillator()
  const g = ctx.createGain()
  osc.type = o.type ?? 'sine'
  osc.frequency.setValueAtTime(o.freq, t0)
  if (o.to) osc.frequency.exponentialRampToValueAtTime(Math.max(20, o.to), t0 + o.dur)
  if (o.detune) osc.detune.setValueAtTime(o.detune, t0)

  const peak = o.gain ?? 0.22
  g.gain.setValueAtTime(0.0001, t0)
  g.gain.exponentialRampToValueAtTime(peak, t0 + Math.min(0.02, o.dur * 0.25))
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + o.dur)

  osc.connect(g).connect(master)
  osc.start(t0)
  osc.stop(t0 + o.dur + 0.03)
}

/** เสียงซ่า ๆ ใช้ทำเสียงเต๋ากระทบ ระเบิด และสัญญาณรบกวน */
function noise(dur: number, gain = 0.15, filter = 1800, delay = 0, sweepTo?: number): void {
  if (!ready() || !ctx || !master) return
  const t0 = ctx.currentTime + delay
  const frames = Math.floor(ctx.sampleRate * dur)
  const buf = ctx.createBuffer(1, frames, ctx.sampleRate)
  const data = buf.getChannelData(0)
  for (let i = 0; i < frames; i++) data[i] = Math.random() * 2 - 1

  const src = ctx.createBufferSource()
  src.buffer = buf
  const bp = ctx.createBiquadFilter()
  bp.type = 'bandpass'
  bp.frequency.setValueAtTime(filter, t0)
  if (sweepTo) bp.frequency.exponentialRampToValueAtTime(Math.max(60, sweepTo), t0 + dur)
  const g = ctx.createGain()
  g.gain.setValueAtTime(gain, t0)
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur)

  src.connect(bp).connect(g).connect(master)
  src.start(t0)
  src.stop(t0 + dur)
}

/** ไล่โน้ตเป็นทำนองสั้น ๆ */
function melody(notes: number[], step: number, type: OscillatorType = 'triangle', gain = 0.2): void {
  notes.forEach((f, i) => tone({ freq: f, dur: step * 1.7, type, gain, delay: i * step }))
}

const C5 = 523.25
const E5 = 659.25
const G5 = 783.99
const C6 = 1046.5
const A4 = 440
const F4 = 349.23
const D4 = 293.66

/* ------------------------------------------------------------------ */
/* เสียงของเกม                                                        */
/* ------------------------------------------------------------------ */

export const sfx = {
  /** กดปุ่มทั่วไป */
  tap: () => tone({ freq: 620, to: 780, dur: 0.06, type: 'triangle', gain: 0.09 }),

  /** ลูกเต๋ากลิ้ง — เสียงกระทบถี่ ๆ ตามด้วยเสียงหยุด */
  dice: () => {
    for (let i = 0; i < 7; i++) noise(0.05, 0.1, 1400 + Math.random() * 1800, i * 0.075)
    noise(0.12, 0.16, 900, 0.58)
    tone({ freq: 300, to: 180, dur: 0.14, type: 'square', gain: 0.09, delay: 0.58 })
  },

  /** หมากขยับหนึ่งช่อง */
  step: () => tone({ freq: 900, to: 1200, dur: 0.05, type: 'sine', gain: 0.07 }),

  /** ได้แต้มคู่ */
  double: () => melody([E5, G5, C6], 0.075, 'triangle', 0.17),

  /** ซื้อโฉนด / ปิดดีลสำเร็จ */
  buy: () => {
    melody([C5, E5, G5], 0.06, 'triangle', 0.18)
    tone({ freq: 1600, dur: 0.18, type: 'sine', gain: 0.05, delay: 0.16 })
  },

  /** เสียงเงินเข้า (Payday, เก็บค่าเช่า, กองกลาง) */
  cashIn: () => {
    melody([G5, C6], 0.07, 'triangle', 0.2)
    tone({ freq: 1975, dur: 0.3, type: 'sine', gain: 0.07, delay: 0.13 })
  },

  /** เสียงเงินออก (จ่ายค่าเช่า ภาษี รายจ่ายจุกจิก) */
  cashOut: () => {
    tone({ freq: A4, to: F4, dur: 0.22, type: 'triangle', gain: 0.18 })
    tone({ freq: A4 / 2, to: F4 / 2, dur: 0.28, type: 'sine', gain: 0.12, delay: 0.04 })
  },

  /** อัปเกรดอาคาร */
  upgrade: () => {
    noise(0.1, 0.12, 700)
    melody([C5, G5, C6], 0.055, 'square', 0.12)
  },

  /** จั่วการ์ด */
  card: () => {
    noise(0.14, 0.09, 2600, 0, 900)
    tone({ freq: 1100, to: 1500, dur: 0.1, type: 'sine', gain: 0.08, delay: 0.06 })
  },

  /** เหตุการณ์ไซไฟระดับโลก — สัญญาณเตือนกวาดความถี่ */
  globalEvent: () => {
    tone({ freq: 180, to: 900, dur: 0.5, type: 'sawtooth', gain: 0.13 })
    tone({ freq: 90, to: 450, dur: 0.55, type: 'square', gain: 0.08, delay: 0.02 })
    noise(0.6, 0.08, 400, 0.05, 3000)
    tone({ freq: 1200, to: 300, dur: 0.4, type: 'sine', gain: 0.1, delay: 0.45 })
  },

  /** โทษรายตัว — เสียงไฟฟ้าดูดสั้น ๆ */
  debuff: () => {
    tone({ freq: 420, to: 90, dur: 0.32, type: 'sawtooth', gain: 0.16 })
    noise(0.22, 0.13, 2200, 0.02, 300)
  },

  /** ได้บัฟ / โล่พลังงาน */
  buff: () => {
    tone({ freq: 500, to: 1400, dur: 0.3, type: 'sine', gain: 0.14 })
    melody([G5, C6], 0.08, 'triangle', 0.14)
  },

  /** ถูกแช่แข็ง / เข้าสถานีกักกัน */
  freeze: () => {
    tone({ freq: 1400, to: 220, dur: 0.5, type: 'sine', gain: 0.14 })
    noise(0.4, 0.07, 5000, 0.05, 800)
    tone({ freq: 160, dur: 0.35, type: 'square', gain: 0.07, delay: 0.3 })
  },

  /** ล้มละลาย */
  bankrupt: () => {
    melody([C5, A4, F4, D4], 0.13, 'triangle', 0.18)
    tone({ freq: 110, to: 55, dur: 0.8, type: 'sawtooth', gain: 0.12, delay: 0.36 })
  },

  /** หลุดพ้นวงจรหนู / ชนะ */
  win: () => {
    melody([C5, E5, G5, C6], 0.11, 'triangle', 0.2)
    melody([E5, G5, C6, 1318.5], 0.11, 'sine', 0.12)
    tone({ freq: C6, dur: 1.1, type: 'sine', gain: 0.1, delay: 0.45 })
    noise(0.9, 0.06, 3000, 0.44, 600)
  },

  /** มีเพื่อนเข้าห้อง */
  join: () => melody([G5, C6], 0.09, 'sine', 0.15),

  /** ถึงตาเราแล้ว (โหมดออนไลน์) */
  yourTurn: () => {
    tone({ freq: 880, dur: 0.12, type: 'sine', gain: 0.16 })
    tone({ freq: 1320, dur: 0.18, type: 'sine', gain: 0.13, delay: 0.13 })
  },
}

export type SfxName = keyof typeof sfx

export function play(name: SfxName): void {
  try {
    sfx[name]()
  } catch {
    /* เสียงพังไม่ควรทำให้เกมพัง */
  }
}
